/**
 * The block that sits below the email/password form on the sign-in and sign-up cards.
 *
 * Order is fixed by `docs/SOCIAL_LOGIN.md`: the form first, then a rule reading `or`, then the
 * provider buttons. A provider whose app is not registered is **absent**, not disabled — a
 * button that dead-ends at a missing client id is a worse answer than no button at all.
 *
 * Async states, all four of them:
 *   pending → two placeholders the size of the buttons, so the layout does not jump when they land
 *   success with providers → the rule and the buttons
 *   success with none, or the tenant switch off → nothing, not even the rule
 *   error → one quiet line with a retry, because the password form above still works
 */

import { useState } from 'react'
import type { ReactElement } from 'react'

import { isApiError } from '../../../lib/api'
import { useT } from '../../../lib/i18n'
import { Skeleton } from '../../../components/ui/Skeleton'
import { useSocialSignInOptions } from './api'
import { ProviderButton } from './ProviderButton'
import type { ProviderSurface } from './ProviderButton'
import { SOCIAL_LABEL, socialStartUrl } from './contract'
import type { SocialProvider } from './contract'

export interface SocialSignInProps {
  /** Where the callback should land the browser once the session exists. App-relative. */
  returnTo?: string
  surface?: ProviderSurface
  className?: string
}

/** A hairline with a word in it. The word is real text — it is read, not decorative. */
const OrRule = ({ label }: { label: string }): ReactElement => (
  <div className="flex items-center gap-3">
    <span className="h-px flex-1 bg-hair" aria-hidden="true" />
    <span className="t-label text-ink-mute">{label}</span>
    <span className="h-px flex-1 bg-hair" aria-hidden="true" />
  </div>
)

export const SocialSignIn = ({ returnTo, surface = 'paper', className }: SocialSignInProps): ReactElement | null => {
  const t = useT()
  const { providers, status, error, retry, tenantDisabled } = useSocialSignInOptions()
  const [leavingFor, setLeavingFor] = useState<SocialProvider | null>(null)

  if (tenantDisabled) return null

  const start = (provider: SocialProvider): void => {
    setLeavingFor(provider)
    // A top-level navigation, not a fetch: the answer is a 302 into the provider's consent
    // screen, and XHR would only meet a CORS wall on the way there.
    window.location.assign(socialStartUrl(provider, returnTo))
  }

  if (status === 'pending') {
    return (
      <div className={className}>
        <div className="mb-4">
          <OrRule label={t('auth.social.or')} />
        </div>
        <div className="flex flex-col gap-3">
          <Skeleton height={44} />
          <Skeleton height={44} />
        </div>
        <span className="sr-only" role="status">
          {t('auth.social.checkingOptions')}
        </span>
      </div>
    )
  }

  if (status === 'error') {
    // A server with no social sign-in at all answers 404 here. That is an answer, not a fault:
    // render nothing and let the password form be the whole card.
    if (isApiError(error) && error.status === 404) return null
    return (
      <div className={className}>
        <p className="t-small flex flex-wrap items-center gap-2 text-ink-mute">
          {t('auth.social.optionsFailed')}
          <button
            type="button"
            onClick={retry}
            className="ring-focus font-semibold text-brass-deep underline underline-offset-2"
          >
            {t('auth.social.checkAgain')}
          </button>
        </p>
      </div>
    )
  }

  if (providers.length === 0) return null

  return (
    <div className={className}>
      <div className="mb-4">
        <OrRule label={t('auth.social.or')} />
      </div>

      <div className="flex flex-col gap-3">
        {providers.map((provider) => (
          <ProviderButton
            key={provider}
            provider={provider}
            surface={surface}
            onSelect={start}
            busy={leavingFor === provider}
            disabled={leavingFor !== null && leavingFor !== provider}
          />
        ))}
      </div>

      <p className="t-small mt-2 h-4 text-ink-mute" role="status" aria-live="polite">
        {leavingFor ? t('auth.social.takingYouTo', { provider: SOCIAL_LABEL[leavingFor] }) : ''}
      </p>
    </div>
  )
}
